import { Apartment, DeleteOutline, LocationCity } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AemterNavigate } from './aemterNav';
import React, { useEffect, useState } from 'react';
import {
  useCreateAdminUnitMunicipality,
  useDeleteAdminUnitMunicipality,
  usePostcodesByMunicipality,
  useAssignPostcodeMunicipality,
  useUpdateMunicipality,
} from '../../Aemter/hooks';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import type { IPostcode } from '../../Interfaces/IPostcode';
import { useAlert } from '../../context/AlertContext';
import type { IAdminstrativeUnit } from '../../Interfaces/IAdminstrativeUnit';
import type { IFederalState } from '../../Interfaces/IAemter';
import type {
  IMunicipality,
  IAdminUnitMunicipality,
} from '../../Interfaces/IMunicipality';

interface IProps {
  municipality: IMunicipality;
  federalStates: IFederalState[];
  administrativeUnits: IAdminstrativeUnit[];
  mappings: IAdminUnitMunicipality[];
  /** Karte „Gerichte & Behörden dieser Gemeinde" — wird zwischen Stammdaten und
   *  Gemarkungen eingehängt (Reihenfolge: Gemeinde → Gerichte/Behörden →
   *  Gemarkung → PLZ). */
  officesSlot?: React.ReactNode;
  onAnyChange: () => void;
  onNavigate?: AemterNavigate;
}

/**
 * Detail-Pane für die politische Gemeinde. Reihenfolge:
 *   1. Stammdaten (Gemeinde)
 *   2. Gerichte & Behörden (officesSlot — direkte Zuordnung an der Gemeinde)
 *   3. Zugeordnete Gemarkungen
 *   4. Postleitzahlen
 *
 * Die früher hier gezeigte, über die Gemarkungen aggregierte Zuständigkeiten-
 * Karte entfällt: Gerichte/Behörden hängen jetzt direkt an der Gemeinde (bzw.
 * PLZ), das Grundbuchamt an der Gemarkung.
 */
export const MunicipalityDetailPane: React.FC<IProps> = ({
  municipality,
  federalStates,
  administrativeUnits,
  mappings,
  officesSlot,
  onAnyChange,
  onNavigate,
}) => {
  const { showAlert } = useAlert();
  const { createAdminUnitMunicipality, isPendingCreateAdminUnitMunicipality } =
    useCreateAdminUnitMunicipality();
  const { deleteAdminUnitMunicipality } = useDeleteAdminUnitMunicipality();
  const {
    municipalityPostcodes,
    isLoadingMunicipalityPostcodes,
    refetchMunicipalityPostcodes,
  } = usePostcodesByMunicipality(municipality.idMunicipality);
  const { assignPostcodeMunicipality, isPendingAssignPostcodeMunicipality } =
    useAssignPostcodeMunicipality();
  const { updateMunicipality, isPendingUpdateMunicipality } =
    useUpdateMunicipality();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerGemarkungId, setPickerGemarkungId] = useState<number | ''>('');
  const [postcodeToAdd, setPostcodeToAdd] = useState<IPostcode | null>(null);

  // Kontakt der Gemeinde-/Stadtverwaltung (editierbar).
  const [contact, setContact] = useState({
    Email: '',
    Street: '',
    StreetNr: '',
    Postcode: '',
    Website: '',
    PhoneNumber: '',
  });
  useEffect(() => {
    setContact({
      Email: municipality.Email ?? '',
      Street: municipality.Street ?? '',
      StreetNr: municipality.StreetNr ?? '',
      Postcode: municipality.Postcode ?? '',
      Website: municipality.Website ?? '',
      PhoneNumber: municipality.PhoneNumber ?? '',
    });
  }, [municipality]);

  const handleSaveContact = async () => {
    try {
      await updateMunicipality({ ...municipality, ...contact });
      showAlert({ text: 'Kontaktdaten gespeichert' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const fsName =
    federalStates.find(f => f.idFederalState === municipality.idFederalState)
      ?.FederalState ?? '';

  const myMappings = mappings.filter(
    m =>
      m.idMunicipality === municipality.idMunicipality &&
      m.activeAssignment !== false
  );
  const mappedGemarkungIds = new Set(
    myMappings.map(m => m.idAdministrativeUnit)
  );

  const gemarkungName = (id: number) =>
    administrativeUnits.find(u => u.idAdministrativeUnit === id)
      ?.AdministrativeUnit ?? `Gemarkung ${id}`;

  // Zuordenbare Gemarkungen: gleiches Bundesland, noch nicht verknüpft.
  const addableGemarkungen = administrativeUnits
    .filter(
      u =>
        u.idFederalState === municipality.idFederalState &&
        !mappedGemarkungIds.has(u.idAdministrativeUnit)
    )
    .sort((a, b) => a.AdministrativeUnit.localeCompare(b.AdministrativeUnit));

  const handleAddGemarkung = async (idAdministrativeUnit: number) => {
    try {
      await createAdminUnitMunicipality({
        idAdminUnitMunicipality: -1,
        idAdministrativeUnit,
        idMunicipality: municipality.idMunicipality,
        activeAssignment: true,
      });
      showAlert({ text: 'Gemarkung zugeordnet' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const handleRemoveMapping = async (m: IAdminUnitMunicipality) => {
    try {
      await deleteAdminUnitMunicipality(m.idAdminUnitMunicipality);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  const submitPicker = async () => {
    if (typeof pickerGemarkungId !== 'number') return;
    await handleAddGemarkung(pickerGemarkungId);
    setPickerGemarkungId('');
    setPickerOpen(false);
  };

  // --- PLZ↔Gemeinde (eine PLZ gehört zu genau EINER Gemeinde) ---
  const handleAddPostcode = async () => {
    if (!postcodeToAdd?.idPostcode) return;
    // Warnen, falls die PLZ bereits einer ANDEREN Gemeinde zugeordnet ist.
    if (
      postcodeToAdd.idMunicipality &&
      postcodeToAdd.idMunicipality !== municipality.idMunicipality &&
      !window.confirm(
        `Die PLZ ${postcodeToAdd.Postcode} ist aktuell „${
          postcodeToAdd.Municipality ?? 'einer anderen Gemeinde'
        }" zugeordnet. Zu „${municipality.Municipality}" verschieben?`
      )
    ) {
      return;
    }
    try {
      await assignPostcodeMunicipality({
        idPostcode: postcodeToAdd.idPostcode,
        idMunicipality: municipality.idMunicipality,
      });
      showAlert({ text: 'PLZ zugeordnet' });
      setPostcodeToAdd(null);
      onAnyChange();
      void refetchMunicipalityPostcodes();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const handleRemovePostcode = async (idPostcode: number) => {
    try {
      await assignPostcodeMunicipality({ idPostcode, idMunicipality: null });
      showAlert({ text: 'PLZ-Zuordnung entfernt' });
      onAnyChange();
      void refetchMunicipalityPostcodes();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  return (
    <Box>
      {/* 1. Stammdaten */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center'>
            <LocationCity color='primary' />
            <Box>
              <Typography variant='h6'>{municipality.Municipality}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {fsName}
                {municipality.AGS ? ` · AGS ${municipality.AGS}` : ''}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* 1b. Kontakt der Gemeinde-/Stadtverwaltung */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Gemeinde-/Stadtverwaltung
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction='row' spacing={1}>
              <TextField
                size='small'
                label='Straße'
                value={contact.Street}
                onChange={e =>
                  setContact(c => ({ ...c, Street: e.target.value }))
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size='small'
                label='Nr.'
                value={contact.StreetNr}
                onChange={e =>
                  setContact(c => ({ ...c, StreetNr: e.target.value }))
                }
                sx={{ width: 90 }}
              />
            </Stack>
            <Stack direction='row' spacing={1}>
              <TextField
                size='small'
                label='PLZ'
                value={contact.Postcode}
                onChange={e =>
                  setContact(c => ({ ...c, Postcode: e.target.value }))
                }
                sx={{ width: 120 }}
              />
              <TextField
                size='small'
                label='Telefon'
                value={contact.PhoneNumber}
                onChange={e =>
                  setContact(c => ({ ...c, PhoneNumber: e.target.value }))
                }
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              size='small'
              label='E-Mail'
              value={contact.Email}
              onChange={e => setContact(c => ({ ...c, Email: e.target.value }))}
              fullWidth
            />
            <TextField
              size='small'
              label='Website'
              value={contact.Website}
              onChange={e =>
                setContact(c => ({ ...c, Website: e.target.value }))
              }
              fullWidth
            />
            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant='outlined'
                onClick={() => void handleSaveContact()}
                disabled={isPendingUpdateMunicipality}
              >
                Kontakt speichern
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* 2. Gerichte & Behörden (direkt an der Gemeinde) */}
      {officesSlot && <Box sx={{ mb: 2 }}>{officesSlot}</Box>}

      {/* 3. Zugeordnete Gemarkungen */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle1' fontWeight={600}>
              Zugeordnete Gemarkungen
            </Typography>
            <Button
              size='small'
              variant='outlined'
              onClick={() => {
                setPickerGemarkungId('');
                setPickerOpen(true);
              }}
              disabled={addableGemarkungen.length === 0}
            >
              Gemarkung hinzufügen
            </Button>
          </Stack>
          {myMappings.length === 0 ? (
            <Alert severity='info' variant='outlined'>
              Noch keine Gemarkungen zugeordnet. Über die Gemarkungen wird u. a.
              das zuständige Grundbuchamt abgeleitet.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {myMappings
                .slice()
                .sort((a, b) =>
                  gemarkungName(a.idAdministrativeUnit).localeCompare(
                    gemarkungName(b.idAdministrativeUnit)
                  )
                )
                .map(m => (
                  <Chip
                    key={m.idAdminUnitMunicipality}
                    icon={<Apartment />}
                    label={gemarkungName(m.idAdministrativeUnit)}
                    size='small'
                    variant='outlined'
                    clickable={!!onNavigate}
                    onClick={
                      onNavigate
                        ? () =>
                            onNavigate({
                              kind: 'gemarkung',
                              id: m.idAdministrativeUnit,
                            })
                        : undefined
                    }
                    onDelete={() => void handleRemoveMapping(m)}
                    deleteIcon={
                      <Tooltip title='Zuordnung entfernen'>
                        <DeleteOutline />
                      </Tooltip>
                    }
                  />
                ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 4. Postleitzahlen */}
      <Card variant='outlined'>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Postleitzahlen
            {municipalityPostcodes ? ` (${municipalityPostcodes.length})` : ''}
          </Typography>
          {isLoadingMunicipalityPostcodes ? (
            <CircularProgress size={22} />
          ) : municipalityPostcodes && municipalityPostcodes.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {municipalityPostcodes.map(p => (
                <Chip
                  key={p.idPostcode}
                  size='small'
                  variant='outlined'
                  clickable={!!onNavigate}
                  onClick={
                    onNavigate
                      ? () =>
                          onNavigate({
                            kind: 'postcode',
                            postcode: {
                              idPostcode: p.idPostcode,
                              Postcode: p.Postcode,
                              City: p.City,
                              idCountry: null,
                              idMunicipality: municipality.idMunicipality,
                              Municipality: municipality.Municipality,
                              AGS: municipality.AGS ?? null,
                            },
                          })
                      : undefined
                  }
                  onDelete={() => void handleRemovePostcode(p.idPostcode)}
                  deleteIcon={
                    <Tooltip title='PLZ aus dieser Gemeinde entfernen'>
                      <DeleteOutline />
                    </Tooltip>
                  }
                  label={`${p.Postcode} ${p.City}`}
                />
              ))}
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Keine Postleitzahlen zugeordnet.
            </Typography>
          )}

          {/* PLZ hinzufügen (eine PLZ gehört zu genau einer Gemeinde). */}
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ mt: 1.5 }}
          >
            <Box sx={{ flex: 1 }}>
              <PostcodeSearch
                postcodeObject={postcodeToAdd}
                updatePostcodeObject={p => setPostcodeToAdd(p ?? null)}
                required={false}
              />
            </Box>
            <Button
              variant='outlined'
              onClick={() => void handleAddPostcode()}
              disabled={
                !postcodeToAdd?.idPostcode ||
                isPendingAssignPostcodeMunicipality
              }
            >
              Zuordnen
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Picker: Gemarkung hinzufügen */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Gemarkung zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Gemarkung'
            value={pickerGemarkungId === '' ? '' : String(pickerGemarkungId)}
            onChange={e => setPickerGemarkungId(Number(e.target.value))}
            fullWidth
            sx={{ mt: 1 }}
          >
            {addableGemarkungen.map(u => (
              <MenuItem
                key={u.idAdministrativeUnit}
                value={u.idAdministrativeUnit}
              >
                {u.AdministrativeUnit}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={() => void submitPicker()}
            disabled={
              typeof pickerGemarkungId !== 'number' ||
              isPendingCreateAdminUnitMunicipality
            }
          >
            Zuordnen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
